import React from 'react';
import './Switch.css';

export interface SwitchProps {
  /** Whether the switch is on. */
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Text beside the switch. Clicking it toggles, like a real label. */
  label?: React.ReactNode;
  /** Small print under the label, for what the setting actually does. */
  description?: React.ReactNode;
  disabled?: boolean;
  /** 'sm' for dense settings lists, 'md' (default) elsewhere. */
  size?: 'sm' | 'md';
  /** Put the label first and the switch at the far right, for settings rows. */
  labelPosition?: 'right' | 'left';
  id?: string;
  className?: string;
  /** Accessible name when there is no visible label. */
  'aria-label'?: string;
}

/**
 * The one on/off switch (OEL-173).
 *
 * Five screens each had their own: two rendered a plain ON/OFF button, one drew
 * a hardcoded green track that ignored the theme, and the Front CMS one was a
 * white knob on a white background when off, which on the light theme made an
 * off switch invisible (OEL-138).
 *
 * This one is a real checkbox underneath, so it is keyboard-reachable, has a
 * focus ring and announces its state to a screen reader. The off state keeps a
 * border and a tinted knob so it is visible in both themes.
 */
export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
  labelPosition = 'right',
  id,
  className = '',
  'aria-label': ariaLabel,
}: SwitchProps) {
  const autoId = React.useId();
  const switchId = id || `ui-switch-${autoId}`;

  return (
    <div className={`ui-switch-row ui-switch-${size} ui-switch-label-${labelPosition} ${disabled ? 'is-disabled' : ''} ${className}`}>
      <label className="ui-switch-labelwrap" htmlFor={switchId}>
        <input
          type="checkbox"
          role="switch"
          id={switchId}
          className="ui-switch-input"
          checked={checked}
          disabled={disabled}
          aria-label={ariaLabel}
          onChange={e => onChange(e.target.checked)}
        />
        <span className="ui-switch-track" aria-hidden="true">
          <span className="ui-switch-knob" />
        </span>
        {(label || description) && (
          <span className="ui-switch-text">
            {label && <span className="ui-switch-title">{label}</span>}
            {description && <span className="ui-switch-desc">{description}</span>}
          </span>
        )}
      </label>
    </div>
  );
}
